import React, { useEffect, useRef, useState } from "react";

// Clip playback stays mounted when the complete video becomes available.
const SegmentPreview = ({ progress, normalizeUrl, isMuted, fullVideoUrl, onPlayFullVideo }) => {
  const urls = (progress.preview_segment_urls || []).map(normalizeUrl);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [downloadRequested, setDownloadRequested] = useState(false);
  const [failedUrl, setFailedUrl] = useState(null);
  const [playback, setPlayback] = useState({ url: null, fraction: 0 });
  const downloadRef = useRef(null);
  const videoRef = useRef(null);
  const firstReady = urls.findIndex(Boolean);
  const activeIndex = selectedIndex === null ? firstReady : selectedIndex;
  const currentUrl = urls[activeIndex];
  let nextIndex = activeIndex + 1;
  while (progress.segment_statuses?.[nextIndex] === "failed") nextIndex += 1;
  const nextUrl = urls[nextIndex];
  const total = progress.total_scene_count || urls.length;
  const finishedClips = progress.completed_scene_count >= total;

  useEffect(() => {
    if (selectedIndex === null && firstReady >= 0) setSelectedIndex(firstReady);
    if (autoAdvance && waiting && nextUrl) {
      setSelectedIndex(nextIndex);
      setWaiting(false);
    }
  }, [selectedIndex, firstReady, autoAdvance, waiting, nextUrl, nextIndex]);

  // Keep one media element across clips so its sound settings and autoplay
  // permission survive source changes. Retry when media becomes playable if
  // the initial play request was interrupted while the source was loading.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !currentUrl) return;
    let cancelled = false;
    let started = false;
    let pending = false;
    const startPlayback = async () => {
      if (cancelled || started || pending) return;
      pending = true;
      try {
        await video.play();
        if (!cancelled) started = true;
      } catch (error) {
        if (cancelled) return;
        if (error?.name === "NotAllowedError") {
          // Fresh pages may disallow sound, but allow muted video autoplay.
          video.muted = true;
          try {
            await video.play();
            if (!cancelled) started = true;
          } catch (_) {
            // Native controls remain available if the browser blocks playback.
          }
        }
      } finally {
        pending = false;
      }
    };
    video.addEventListener("canplay", startPlayback);
    startPlayback();
    return () => {
      cancelled = true;
      video.removeEventListener("canplay", startPlayback);
    };
  }, [currentUrl]);

  useEffect(() => {
    if (downloadRequested && fullVideoUrl) {
      downloadRef.current?.click();
      setDownloadRequested(false);
    }
  }, [downloadRequested, fullVideoUrl]);

  if (!currentUrl) return null;
  return (
    <div className="video-player segment-preview">
      <div className="video-frame" style={{ aspectRatio: "16 / 9" }}>
        <video
          ref={videoRef}
          src={currentUrl}
          controls
          controlsList="nodownload"
          autoPlay
          playsInline
          preload="auto"
          muted={isMuted}
          aria-label={`第 ${activeIndex + 1} 段预览`}
          onTimeUpdate={event => {
            const { currentTime, duration } = event.currentTarget;
            setPlayback({ url: currentUrl, fraction: Number.isFinite(duration) && duration > 0 ? Math.min(1, currentTime / duration) : 0 });
          }}
          onEnded={() => {
            if (autoAdvance && nextUrl) {
              setSelectedIndex(nextIndex);
              setWaiting(false);
            } else setWaiting(true);
          }}
          onError={() => setFailedUrl(currentUrl)}
        >
          您的浏览器不支持视频播放
        </video>
      </div>
      {nextUrl && <video src={nextUrl} preload="auto" muted playsInline hidden style={{ display: "none" }} aria-hidden="true" />}
      <div className="segment-preview-panel">
        <div className="segment-preview-list segment-timeline" aria-label="播放进度">
          {urls.map((url, index) => {
            const state = progress.segment_statuses?.[index];
            const active = index === activeIndex;
            const fraction = active ? (playback.url === currentUrl ? playback.fraction : 0) : index < activeIndex && url ? 1 : 0;
            return (
              <button
                type="button"
                key={index}
                className={`segment-timeline-step${active ? " is-active" : ""}${url ? " is-ready" : ""}${state === "failed" ? " is-unavailable" : ""}`}
                disabled={!url}
                aria-pressed={active}
                aria-label={`跳转至第 ${index + 1} 段${state === "failed" ? "，暂不可用" : !url ? "，生成中" : ""}`}
                title={state === "failed" ? "暂不可用" : !url ? "内容生成中" : "点击跳转"}
                onClick={() => { setSelectedIndex(index); setWaiting(false); }}
                style={{ "--segment-progress": `${fraction * 100}%` }}
              >
                <span className="segment-timeline-track"><span className="segment-timeline-fill" /></span>
              </button>
            );
          })}
        </div>
        <div className="segment-preview-toolbar">
          <p className={`segment-preview-status${fullVideoUrl ? " is-complete" : ""}${progress.full_video_status === "failed" ? " is-warning" : ""}`} role="status">
            <span className="segment-status-dot" aria-hidden="true" />
            {fullVideoUrl ? "已就绪" : progress.full_video_status === "failed" ? "完整视频合并失败，仍可预览" : progress.phase === "finalizing" ? "即将就绪" : "边看边生成"}
          </p>
          <div className="segment-preview-actions">
            <label className="segment-autoplay" title="自动连续播放">
              <input type="checkbox" checked={autoAdvance} onChange={event => setAutoAdvance(event.target.checked)} />
              <span className="segment-toggle" aria-hidden="true" />
              <span>连播</span>
            </label>
            {nextUrl && <button className="segment-icon-button" type="button" aria-label="下一段" title="继续播放"
              onClick={() => { setSelectedIndex(nextIndex); setWaiting(false); }}><i className="ri-skip-forward-mini-fill" aria-hidden="true" /></button>}
            {fullVideoUrl && <button className="segment-text-button" type="button" onClick={onPlayFullVideo}>播放完整视频</button>}
            {fullVideoUrl ? (
              <a ref={downloadRef} className="video-download-btn segment-download" href={fullVideoUrl} download aria-label="下载完整视频" title="下载完整视频">
                <i className="ri-download-2-line" aria-hidden="true" /><span>下载</span>
              </a>
            ) : (
              <button className="segment-download" type="button" disabled={downloadRequested || progress.full_video_status === "failed"}
                aria-label={downloadRequested ? "完整视频就绪后自动下载" : "下载完整视频"}
                title={downloadRequested ? "准备好后自动下载" : "下载完整视频"}
                onClick={() => setDownloadRequested(true)}>
                <i className={downloadRequested ? "ri-loader-4-line segment-download-spinner" : "ri-download-2-line"} aria-hidden="true" />
                <span>{downloadRequested ? "准备中" : "下载"}</span>
              </button>
            )}
          </div>
        </div>
        {waiting && <p className="segment-preview-hint">{finishedClips && nextIndex >= total ? "已播放完毕，轻点进度条重看" : autoAdvance ? "后续内容就绪后，将自动继续" : "已暂停，轻点继续播放"}</p>}
      </div>
      {failedUrl === currentUrl && (
        <p className="segment-preview-hint">暂时无法加载此片段，可轻点进度条切换内容。</p>
      )}
    </div>
  );
};

export default SegmentPreview;
